// Backend service for production Stripe integration
// This would typically be a separate backend API, but for now we'll use Firebase Functions

// API base URL (same project), choose endpoint per environment
const API_BASE_URL = 'https://us-central1-hammersawpaint.cloudfunctions.net';
import appConfig from '../config/appConfig.json';

const isHttps = typeof window !== 'undefined' && window.location?.protocol === 'https:';
const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';
const productionHosts = new Set(appConfig?.productionHosts || []);
const isProductionHost = productionHosts.has(hostname);

const getEndpoint = (name) => {
  // Use LIVE endpoints only on HTTPS production hosts
  if (isHttps && isProductionHost) return name;
  return `${name}Dev`;
};

// Create payment intent via backend
export const createPaymentIntent = async (paymentData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${getEndpoint('createPaymentIntent')}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    
    // Fallback to mock for development
    if (process.env.NODE_ENV === 'development') {
      return createMockPaymentIntent(paymentData);
    }
    
    throw error;
  }
};

// Create checkout session via backend
export const createCheckoutSession = async (paymentData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${getEndpoint('createCheckoutSession')}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    
    // Fallback to mock for development
    if (process.env.NODE_ENV === 'development') {
      return createMockCheckoutSession(paymentData);
    }
    
    throw error;
  }
};

// Mock functions for development
const createMockPaymentIntent = async (paymentData) => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    id: `pi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    client_secret: `pi_${Date.now()}_secret_${Math.random().toString(36).substr(2, 9)}`,
    amount: paymentData.amount,
    currency: 'usd',
    status: 'requires_payment_method',
    metadata: paymentData.metadata || {}
  };
};

const createMockCheckoutSession = async (paymentData) => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    id: `cs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    url: `https://checkout.stripe.com/pay/cs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    success: true
  };
};

// Update payment status in Firebase
export const updatePaymentStatus = async (paymentIntentId, status, metadata = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/updatePaymentStatus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentIntentId,
        status,
        metadata
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
};

// Confirm Checkout Session (no webhooks) — calls dev/live based on host
export const confirmCheckoutSession = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${getEndpoint('confirmCheckoutSession')}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error confirming checkout session:', error);
    throw error;
  }
};

export default {
  createPaymentIntent,
  createCheckoutSession,
  updatePaymentStatus,
  confirmCheckoutSession
};
